import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocation, useOutlet } from "react-router-dom";

interface PageTransitionProps {
  className?: string;
}

// Animates the matched route element on path change.
// Uses useOutlet() (not <Outlet />) so the exiting node retains the previous
// page's content during exit — otherwise the new page paints first, then
// animates, because <Outlet /> always reads the current router context.
export const PageTransition = ({ className }: PageTransitionProps) => {
  const { pathname } = useLocation();
  const element = useOutlet();
  const reduceMotion = useReducedMotion();

  const variants = reduceMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -4 },
      };

  return (
    // initial={false} skips animation on the very first mount (and on any
    // Suspense remount). This kills the "fade flicker" perceived on cold loads
    // — the page just appears at its final state. Subsequent key changes
    // (route navigation while AP is mounted) still animate normally.
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={pathname}
        initial={variants.initial}
        animate={variants.animate}
        exit={variants.exit}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        className={className}
      >
        {element}
      </motion.div>
    </AnimatePresence>
  );
};
